import { useState } from 'react';
import styled from 'styled-components';

import DefaultItem from '@/components/Common/DefaultItem';
import PropertyValue from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import { COMPANY_UNKNOWN } from '@/configs/reserved-word';
import useCompanyStore from '@/features/Account/store';
import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import useDropdownStore from '@/stores/pure/DropdownStore';
import { useToast } from '@/stores/pure/ToastStore';
import { multilineEllipsis } from '@/styles/style';

import CompanySelectModal from '../CompanySelectModal';

export type Props = {
  name: string;
  company_info: CompanyInfoWithAlias | null;
  customer_info?: CustomerInfoWithAlias | null;
  changeCompany: (
    company: CompanyT | null,
    prevState?: {
      customer?: CustomerInfoWithAlias | null;
      company?: CompanyInfoWithAlias | null;
    },
  ) => void;
  clearable?: boolean;
  view: 'list' | 'detail';
};

const CompanyDropdown = (props: Props) => {
  const addCompany = useCompanyStore((state) => state.addCompany);
  const addToast = useToast();
  const { isEditor } = useUserRole();
  const color = useColor();
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);
  const [triggerEl, setTriggerEl] = useState<HTMLDivElement | null>(null);

  return (
    <Style>
      {props.view === 'list' && (
        <DefaultItem
          onClick={(e) => {
            e.stopPropagation();
            if (!isEditor) return;
            setCurrDropdown(props.name);
            setTriggerEl(e.currentTarget);
          }}
        >
          <ItemText
            type="label2"
            weight="r"
            color={props.company_info?.name ? 'inherit' : color.gray500}
          >
            {props.company_info?.name || COMPANY_UNKNOWN}
          </ItemText>
        </DefaultItem>
      )}

      {props.view === 'detail' && (
        <PropertyValue
          style={{
            color: props.company_info?.name ? 'inherit' : color.gray500,
          }}
          value={
            <Typo type="body3" multilineEllipsis={2}>
              {props.company_info?.name || COMPANY_UNKNOWN}
            </Typo>
          }
          onClick={(e) => {
            if (!isEditor) return;
            setCurrDropdown(props.name);
            setTriggerEl(e.currentTarget);
          }}
        />
      )}

      <CompanySelectModal
        name={props.name}
        triggerEl={triggerEl}
        toggleItem={(company) => {
          props.changeCompany(company, {
            customer: props.customer_info,
            company: props.company_info,
          });
          setCurrDropdown(null);
        }}
        clearable
        createNewCompany={async (name: string) => {
          try {
            const company = await addCompany({ name });
            return company;
          } catch (e) {
            addToast('This company name already exists.', 'fail');
            throw e;
          }
        }}
      />
    </Style>
  );
};

const ItemText = styled(Typo)`
  ${multilineEllipsis(2)};
`;

const Style = styled.div`
  position: relative;
`;

export default CompanyDropdown;
